import { CommonModule } from '@angular/common';
import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {  ListTableComponent  } from '@ppl/ui';
import { DatePickerModule } from 'primeng/datepicker';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { TimesheetFilterComponent } from '../timesheet-filter/timesheet-filter.component';
import { LookupService } from '../../../../services/lookup.service';
import { UserService } from '../../../../services/user.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { ListService } from '../../../../shared/services/list.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { PopupDialogComponent } from '@ppl/ui';
import { ButtonComponent } from '@ppl/ui';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { TimesheetService } from '../../../../services/timesheet.service';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { environment } from '../../../../../environments/environment';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { AuthorizationDetailsComponent } from '../../authorizations/authorization-details/authorization-details.component';
import { SectionsService } from '../../../../services/sections.service';

@Component({
  selector: 'ppl-timesheet-payroll-adjustment-approvals',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgZorroModule,
    NzDatePickerModule,
    DatePickerModule,
    ButtonComponent,
    ListTableComponent,
    TimesheetFilterComponent,
    PopupDialogComponent,
    AuthorizationDetailsComponent
  ],
  templateUrl: './timesheet-payroll-adjustment-approvals.component.html',
  styleUrl: './timesheet-payroll-adjustment-approvals.component.css'
})
export class TimesheetPayrollAdjustmentApprovalsComponent {
  @Input() source: string = 'payroll';
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild(TimesheetFilterComponent) timesheetFilterChildComponent!: TimesheetFilterComponent;
  @ViewChild('searchFilterWarningDialog') searchFilterWarningDialog!: TemplateRef<any>;
  @ViewChild('authDialog') authDialog!: TemplateRef<any>;
  // Passed to ppl-authorization-details as an input; never put into the shared selected lead.
  selectedAuthorizationId: any = null;

  private dialogSvc = inject(DialogService);
  private allowListStore = inject(AllowListAuthStore);

  pageId = PAGE_CONSTANTS.STAFF_PAYROLL_ADJUSTMENT_APPROVALS;
  isReadOnly = false;
  isPageRestrictionLoaded = false;

  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  appliedFilters: any = [];
  approvalTimesheetForm!: FormGroup;
  dialogButtonLabel = '';
  commentText = '';
  dialogData: any;
  dialogNoteMessage = '';
  isDialogNoteDisplayed = false;
  programName!: string;
  processingStatusList: any;
  reasonList: any;
  businessStatusList: any;
  selectedAction: any;
  serviceCodes: LookupValueItem[] = [];
  showActionPopup: boolean = false;
  timesheets: any[] = [];
  hasSearched = false;
  // Toggled to destroy + recreate the results table for a clean full refresh.
  listReloading = false;
  title = '';
  chequeNumbers: any[] = [];

  buttons = [
    {
      button_id: "Approve",
      button_label: "Approve",
      buttonAction: "approveTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "ApproveAll",
      button_label: "Approve All",
      buttonAction: "approveAllTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "Reject",
      button_label: "Reject",
      buttonAction: "rejectTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "RejectAll",
      button_label: "Reject All",
      buttonAction: "rejectAllTimesheets",
      className: "primary-btn-color"
    }
  ];

  cols = [
    {
      field: 'serviceDate',
      header: 'Service \nDate',
      jsonpath: 'serviceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceDescription',
      header: 'Service \nCode',
      jsonpath: 'serviceDescription',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'authDesc',
      header: 'Service \nDescription',
      jsonpath: 'authDesc',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerName',
      header: 'Participant \nName',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerPplId',
      header: 'Participant \nPPL ID',
      jsonpath: 'consumerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'providerName',
      header: 'Provider \nName',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerPplId',
      header: 'Provider PPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'timeInTimeOut',
      header: 'Time In - \nTime Out',
      jsonpath: 'timeInTimeOut',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'billedUnitsInHrs',
      header: 'Billed \nHours',
      jsonpath: 'billedUnitsInHrs',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paidUnits',
      header: 'Paid \nHours',
      jsonpath: 'paidUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceRate',
      header: 'Service \nRate',
      jsonpath: 'serviceRate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'businessStatus',
      header: 'Business \nStatus',
      jsonpath: 'businessStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'processingStatus',
      header: 'Processing \nStatus',
      jsonpath: 'processingStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'source_type_lkp',
      header: 'Timesheet \nSource',
      jsonpath: 'source_type_lkp',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'mco_name',
      header: 'Regional \nCenter',
      jsonpath: 'mco_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'tasAuthorizationDetailId',
      header: 'Authorization \nNumber',
      jsonpath: 'tasAuthorizationDetailId',
      isDisplayedAtHeader: 'y',
      isClickable: true,
      isSearchable: 'n',
    },
    {
      field: 'associationStatus',
      header: 'CBA \nAssociation',
      jsonpath: 'associationStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'reason',
      header: 'Reason',
      jsonpath: 'reason',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  lineItemCols = [
    {
      field: 'service_date',
      header: 'Service Date',
      jsonpath: 'service_date',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'service_description',
      header: 'Service Code',
      jsonpath: 'service_description',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'authDesc',
      header: 'Service Description',
      jsonpath: 'authDesc',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumer_name',
      header: 'Participant Name',
      jsonpath: 'consumer_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'participant_ppl_id',
      header: 'Participant PPL ID',
      jsonpath: 'participant_ppl_id',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'provider_name',
      header: 'Provider Name',
      jsonpath: 'provider_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'provider_ppl_id',
      header: 'Provider PPL ID',
      jsonpath: 'provider_ppl_id',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'timeInTimeOut',
      header: 'Time In - Time Out',
      jsonpath: 'timeInTimeOut',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'billedUnitsInHrs',
      header: 'Billed Hours',
      jsonpath: 'billedUnitsInHrs',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paidUnitsInHrs',
      header: 'Paid Hours',
      jsonpath: 'paidUnitsInHrs',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'servicecode_rate',
      header: 'Service Rate',
      jsonpath: 'servicecode_rate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'businessStatus',
      header: 'Business Status',
      jsonpath: 'businessStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'processingStatus',
      header: 'Processing Status',
      jsonpath: 'processingStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'mco_name',
      header: 'Regional Center',
      jsonpath: 'mco_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  // Handler for list-table view user events (clicking PPL IDs)
  onViewUserClicked = (payload: any) => {
    this.userService.setViewUserEvent({
      userId: payload.userId,
      isConsumer: payload.isConsumer
    });
  }

  fields = [
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'servicePeriod', show: true, hideFuturePeriods: true },
    { field: 'payPeriod', show: true, hideFuturePeriods: true },
    { field: 'serviceCode', show: true },
    { field: 'chequeNumber', show: true },
    { field: 'serviceDate', show: true, hideFuturePeriods: true },
  ];

  keyMapping: { [key: string]: string } = {
    Timeframe: "serviceDate",
    serviceDate: "serviceDate",
    payPeriod: "tasMasterPayrollScheduleId",
    consumer: "consumerName",
    consumerPplId: "consumerPplId",
    provider: "providerName",
    providerPplId: "providerPplId",
    chequeNumber: "chequeNumber",
    paymentDate: "tasMasterPayrollScheduleId",
    servicePeriod: "servicePeriodId",
    serviceCode: "serviceCodeDetailId"
  };

  constructor(
    private dateTimeFormatterService: DateTimeFormatterService,
    private errorAlertService: ErrorAlertService,
    private fb: FormBuilder,
    private listService: ListService,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService,
    private sectionsService: SectionsService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.businessStatusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.reasonList = this.lookupService.getLookupValue('Disposition Reason');
    this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');

    this.approvalTimesheetForm = this.fb.group({
      comments: [null]
    });

    this.setPageAccess();
  }

  private setPageAccess() {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }

  searchTimesheets(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20) {
    this.hasSearched = true;

    const adjustedTimesheetPayload = this.buildRequestPayload(
      'staffEditTimesheetEntry',
      'timesheetLineId',
      filters,
      startingPageNumber,
      recordsPerPage
    );

    this.listService
      .getListData(adjustedTimesheetPayload, 'claims')
      .subscribe((data: any) => {
        const records = this.processData(data.responsedata);
        if (isChequeLookUp) {
          if (records && records?.length) {
           this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
        else {
          if (startingPageNumber > 1) {
            this.timesheets = [...this.timesheets, ...records];
          } else {
            this.timesheets = records;
          }
          if (this.timesheets?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
      });
  }

  // Fully recreates the results table (destroy + recreate) so no expanded sub-rows,
  // cached pages, or stale row-detail state linger after an action, then reloads.
  private reloadList() {
    this.listReloading = true;
    this.timesheets = [];
    setTimeout(() => {
      this.listReloading = false;
      this.searchTimesheets(this.appliedFilters);
    });
  }

  searchSubRowTimesheets(filters: any, event: any, startingPageNumber = 1, currentPage = 0) {
    const adjustedTimesheetPayload = this.buildRequestPayload(
      'adjustmentTimesheetList',
      'tas_timesheetline_detail_id',
      filters,
      startingPageNumber
    );

    this.listService
      .getListData(adjustedTimesheetPayload, 'claims')
      .subscribe((data: any) => {
        event.lineItems = data.responsedata;

        event?.lineItems?.forEach((element: any) => {
          element.timeInTimeOut = this.dateTimeFormatterService.convertTo12HourFormat(element.time_in) + ' - ' + this.dateTimeFormatterService.convertTo12HourFormat(element.time_out);
          element.businessStatus = this.businessStatusList.filter((status: any) => status.lkpValueCode === element.business_status_lkp)
            .map((status: any) => status.lkpValueName);
          element.processingStatus = this.processingStatusList.filter((status: any) => status.lkpValueCode === element.prcocessing_status_lkp)
            .map((status: any) => status.lkpValueName);
          element.service_description = this.serviceCodes.find((sc: LookupValueItem) => sc.lkpValueCode === String(element.servicecode_detail_id))?.lkpValueName ?? element.service_description;

          this.convertSubRowUnitsToHours(element);
        });

        event.showLineItem = true;
        event.loaded = true;
      });
  }

  private convertSubRowUnitsToHours(element: any) {
    const inTime = this.dateTimeFormatterService.formatStringToTime(element.time_in);
    const outTime = this.dateTimeFormatterService.formatStringToTime(element.time_out);
    if (inTime && outTime) {
      element.billedUnitsInHrs = this.dateTimeFormatterService.getDuration(inTime, outTime);
    } else {
      element.billedUnitsInHrs = element.billed_units ? this.dateTimeFormatterService.formatUnitsToHoursMinutes(element.billed_units * 15) : null;
    }
    element.paidUnitsInHrs = element.paid_units ? this.dateTimeFormatterService.formatUnitsToHoursMinutes(element.paid_units) : null;
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };

    return payload;
  }

  processData(data: any) {
    if (data && data.length > 0) {
      data.forEach((element: any) => {
        element.timeInTimeOut = this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) + ' - ' + this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut),
          element.businessStatus = this.businessStatusList.filter((status: any) => status.lkpValueCode === element.businessStatusLkp)
            .map((status: any) => status.lkpValueName),
          element.processingStatus = this.processingStatusList.filter((status: any) => status.lkpValueCode === element.processingStatusLkp)
            .map((status: any) => status.lkpValueName)

        this.convertUnitsToHours(element);
      });
    }

    return data;
  }

  private convertUnitsToHours(element: any) {
    const inTime = this.dateTimeFormatterService.formatStringToTime(element.timeIn);
    const outTime = this.dateTimeFormatterService.formatStringToTime(element.timeOut);
    if (inTime && outTime) {
      element.billedUnitsInHrs = this.dateTimeFormatterService.getDuration(inTime, outTime);
    } else {
      element.billedUnitsInHrs = element.billedUnits ? this.dateTimeFormatterService.formatUnitsToHoursMinutes(element.billedUnits * 15) : null;
    }
    element.paidUnits = element.paidUnits ? this.dateTimeFormatterService.formatUnitsToHoursMinutes(element.paidUnits) : null;
  }

  onSearch(event: any) {
    const filters = [];

    if (event.Timeframe) {
      filters.push({
        jsonPath: "serviceDate",
        value: this.timeframeToDateString(event.Timeframe),
        operator: "greaterThan"
      });
    }

    if (event.serviceDate) {
      filters.push({
        jsonPath: "serviceDate",
        value: this.dateTimeFormatterService.formatDateToString(event.serviceDate as Date),
        operator: "Equal"
      });
    }

    if (event.payPeriod && event.payPeriod.length > 0) {
      filters.push({
        jsonPath: "tasMasterPayrollScheduleId",
        values: event.payPeriod,
        operator: "In"
      });
    }

    if (event.consumer && event.consumer.trim()) {
      filters.push({
        jsonPath: "consumerName",
        value: event.consumer.trim(),
        operator: "equal"
      });
    }

    if (event.consumerPplId && event.consumerPplId.trim()) {
      filters.push({
        jsonPath: "consumerPplId",
        value: event.consumerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.provider && event.provider.trim()) {
      filters.push({
        jsonPath: "providerName",
        value: event.provider.trim(),
        operator: "equal"
      });
    }

    if (event.providerPplId && event.providerPplId.trim()) {
      filters.push({
        jsonPath: "providerPplId",
        value: event.providerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.chequeNumber) {
      // Normalize the input — array, single string, or comma-separated string — into a clean string[].
      const chequeValues: string[] = Array.isArray(event.chequeNumber)
        ? event.chequeNumber.map((v: any) => String(v).trim()).filter(Boolean)
        : String(event.chequeNumber).split(',').map(v => v.trim()).filter(Boolean);

      if (chequeValues.length) {
        filters.push({
          jsonPath: "chequeNumber",
          values: chequeValues,
          operator: "In"
        });
      }
    }

    if (event.payPeriod) {
      filters.push({
        jsonPath: "tasMasterPayrollScheduleId",
        value: event.payPeriod,
        operator: "Equal"
      });
    }

    if (event.serviceCode) {
      filters.push({
        jsonPath: "serviceCodeDetailId",
        value: event.serviceCode,
        operator: "Equal"
      });
    }

    if (event.servicePeriod) {
      filters.push(
        { 
          jsonPath: 'servicePeriodId',
          value: event.servicePeriod,
          operator: 'Equal' 
        });
    }

    filters.push({
      jsonPath: "processingStatusLkp",
      values: ['VI', 'AI'],
      operator: "IN"
    });

    this.appliedFilters = filters;
    this.reloadList();
  }

  onPageLoad(event: any) {
    this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
  }

  onCellClicked(event: any) {
    const col = event?.col;
    const rowData = event?.rowData;
    if (col?.field === 'tasAuthorizationDetailId' && rowData?.tasAuthorizationDetailId) {
      this.openAuthDialog(rowData);
    }
  }

  openAuthDialog(rowData: any) {
    this.selectedAuthorizationId = rowData.tasAuthorizationDetailId;
    const dialogConfig: DialogConfig = {
      title: `Auth Details for ${rowData.tasAuthorizationDetailId}`,
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };
    this.dialogSvc.openDialog(this.authDialog, dialogConfig, () => { }, () => { });
  }

  private timeframeToDateString(value: string): string {
    const days = parseInt(value, 10);
    const date = new Date();
    date.setDate(date.getDate() - (isNaN(days) ? 30 : days));
    return date.toLocaleDateString('en-US');
  }

  openWarningMessage() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      showOkButton: true,
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.searchFilterWarningDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  rowExpanded(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }

    const subRowFilter = [];
    subRowFilter.push({
      jsonPath: "parent_tas_timesheetline_detail_id",
      value: event.timesheetLineId,
      operator: "Equal"
    });

    this.searchSubRowTimesheets(subRowFilter, event);
  }

  areArraysEqual(a: any[], b: any[]): boolean {
    if (!Array.isArray(a) || !Array.isArray(b))
      return false;

    if (a.length !== b.length)
      return false;

    return a.every((val, i) => val === b[i]);
  }

  isFilterValid(timesheetFilterValues: any, searchCallbackFilterValues: any) {
    const activeFilters = Object.keys(timesheetFilterValues).filter(key => {
      const value = timesheetFilterValues[key];
      if (value === null || value === undefined)
        return false;

      if (typeof value === 'string' && value.trim() === '')
        return false;

      if (Array.isArray(value) && value.length === 0)
        return false;

      return true;
    });

    const userAppliedFilters = searchCallbackFilterValues.filter(
      (filter: any) => !['businessStatusLkp', 'serviceDuration', 'processingStatusLkp'].includes(filter.jsonPath)
    );

    if (activeFilters.length !== userAppliedFilters.length)
      return false;

    return Object.entries(timesheetFilterValues)
      .filter(([_, value]) => value !== null && value !== "")
      .every(([key, value]: [any, any]) => {
        const mappedKey = this.keyMapping[key] || key;

        if (Array.isArray(value) && value.length === 0)
          return true;

        let comparisonValue: any;

        if (key === 'Timeframe') {
          comparisonValue = this.timeframeToDateString(value as string);
        }
        else if (mappedKey === 'serviceDate') {
          comparisonValue = this.dateTimeFormatterService.formatDateToString(value as Date);
        }
        else if (mappedKey === 'serviceDuration') {
          comparisonValue = value.substr(0, value.indexOf(' '));
        } else {
          comparisonValue = value;
        };

        return searchCallbackFilterValues.some((filter: any) => filter.jsonPath === mappedKey &&
          (Array.isArray(filter.value || filter.values) ? this.areArraysEqual(filter.values, comparisonValue) :
            typeof filter?.value === 'string' ? filter?.value?.trim() === comparisonValue?.trim() : filter?.value === comparisonValue));
      });
  }

  gridAction(event: any) {
    if (this.isReadOnly) return;
    const { button, selectedCheckBox } = event;

    if (!this.isFilterValid(this.timesheetFilterChildComponent.timesheetSearchForm.value, this.appliedFilters)) {
      const errorMessage = `Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (this.appliedFilters.length === 0 && (button.buttonAction === 'approveAllTimesheet' || button.buttonAction === 'rejectAllTimesheets')) {
      const errorMessage = `${button.button_label} action requires selection of filters. Kindly apply atleast one filter and perform the search with updated filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox && selectedCheckBox.length === 0
      && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Kindly select checkbox and then perform this action.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox.length > 1 && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Approve / Reject operation can be performed on single timesheet at a time.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (button) {
      this.dialogData = { payload: {} };
      switch (button.buttonAction) {
        case 'approveTimesheet':
        case 'rejectTimesheet': {
          const selectedTimesheet = selectedCheckBox[0];
          this.dialogData.payload.timesheetIds = [selectedTimesheet.timesheetLineId];
          this.isDialogNoteDisplayed = false;
          break;
        }
        case 'approveAllTimesheet':
        case 'rejectAllTimesheets': {
          if (selectedCheckBox.length > 0) {
            this.dialogData.payload.timesheetIds = selectedCheckBox.map((entry: any) => entry.timesheetLineId);
            this.dialogNoteMessage = `Note: The ${button.button_label} action will be performed on all selected timesheets.`
          } else {
            this.dialogData.payload.searchParam = this.appliedFilters;
            this.dialogNoteMessage = `Note: The ${button.button_label} action will be performed on all timesheets matching the selected search criteria.`;
          }
          this.isDialogNoteDisplayed = true;
          break;
        }
      }

      this.dialogData.button = button;
      this.dialogButtonLabel = button.button_label;
      this.title = `${this.source.trim().charAt(0).toUpperCase() + this.source.trim().slice(1)} Adjustment ${button.button_label}`;
      this.commentText = button.button_label.indexOf('Approve') > -1 ? 'Approval comment' : 'Rejection comment *';

      if (button.buttonAction === 'rejectTimesheet' || button.buttonAction === 'rejectAllTimesheets') {
        this.approvalTimesheetForm.get('comments')?.setValidators([Validators.required]);
      } else {
        this.approvalTimesheetForm.get('comments')?.setValidators(null);
      }
      this.approvalTimesheetForm.get('comments')?.setValue(null);
      this.approvalTimesheetForm.markAsUntouched();
      this.approvalTimesheetForm.get('comments')?.updateValueAndValidity();
      this.showActionPopup = true;
    }
  }

  onSave() {
    if (this.isReadOnly) return;
    if (this.approvalTimesheetForm.valid) {
      let placeholder = '';
      const urlEndpoint = this.source === 'payroll' ? ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_DECISION : ENDPOINTS.CLAIMS.BILLING_TIMESHEET_DECISION;
      const payload = this.dialogData.payload;
      payload.comments = this.approvalTimesheetForm?.get('comments')?.value;

      switch (this.dialogData.button.buttonAction) {
        case 'approveTimesheet':
        case 'approveAllTimesheet': {
          payload.decision = 'A';
          placeholder = 'Timesheet approved';
          break;
        }
        case 'rejectTimesheet':
        case 'rejectAllTimesheets': {
          payload.decision = 'R';
          placeholder = 'Timesheet rejected';
          break;
        }
      }

      if (urlEndpoint) {
        this.timesheetService.adjustVoidTimesheet(urlEndpoint, this.programName, payload, placeholder)
          .subscribe((data: any) => {
            this.dialogData = null;
            this.showActionPopup = false;
            this.approvalTimesheetForm.reset();
            this.reloadList();
            // Clear the grid's checkbox selection so the just-actioned row doesn't carry
            // over and combine with the next selection (which would trip the
            // "single timesheet at a time" guard on the following Approve/Reject).
            if (this.childComponent) {
              this.childComponent.selectedCheckBoxValue = [];
            }
            this.searchTimesheets(this.appliedFilters);
          });
      }
    } else {
      this.approvalTimesheetForm.markAllAsTouched();
    }
  }
}
